import React, { useEffect, useState } from "react";
import { X, Trophy, Lock } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import TeamFlag from "./TeamFlag";
import { parseDate } from "../lib/utils";
import { useActiveGroup } from "../contexts/ActiveGroupContext";

function formatDate(iso, locale) {
  const d = parseDate(iso);
  if (!d) return "";
  return d.toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", { day: "numeric", month: "short" });
}

export default function UserBetsModal({ userId, tournamentId, onClose }) {
  const { t, locale } = useI18n();
  const { activeGroup } = useActiveGroup();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${userId}/bets`, {
          params: {
            tournament_id: tournamentId,
            group_id: activeGroup?.group_id,
          },
        });
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || t("could_not_load"));
      }
    })();
  }, [userId, tournamentId, activeGroup?.group_id]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
        data-testid="user-bets-modal"
      >
        <motion.div
          initial={{ y: 60 }}
          animate={{ y: 0 }}
          exit={{ y: 60 }}
          transition={{ type: "spring", damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[86vh] flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-3 p-5 border-b border-neutral-100">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-neutral-100 border border-neutral-200 flex items-center justify-center">
              {data?.user?.picture ? (
                <img src={data.user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-lg text-neutral-500">{(data?.user?.name || "?")[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-tight truncate">{data?.user?.name || "—"}</div>
              <div className="text-[11px] font-bold tracking-wider uppercase text-neutral-400">
                {data ? `${data.total_points} ${t("pts")} · ${data.count} ${t("tips_count")}` : "—"}
              </div>
            </div>
            <button
              type="button"
              data-testid="close-user-bets"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {error && <div className="text-sm text-destructive p-4">{error}</div>}
            {!data && !error && (
              <div className="space-y-3 animate-pulse">
                {/* Bonus bet skeleton */}
                <div className="rounded-xl border border-neutral-100 bg-neutral-50/50 p-3 flex justify-between items-center">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-neutral-100 border border-neutral-200" />
                    <div className="space-y-1">
                      <div className="h-2 bg-neutral-200 rounded w-16" />
                      <div className="h-3.5 bg-neutral-200 rounded w-28" />
                    </div>
                  </div>
                  <div className="h-3.5 bg-neutral-200 rounded w-12" />
                </div>
                {/* Match predictions skeleton list */}
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-neutral-200 p-3">
                    <div className="flex justify-between items-center mb-2">
                      <div className="h-2.5 bg-neutral-200 rounded w-14" />
                      <div className="h-2.5 bg-neutral-200 rounded w-10" />
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 bg-neutral-100 border border-neutral-200 rounded-full flex-shrink-0" />
                        <div className="h-3 bg-neutral-200 rounded w-16" />
                      </div>
                      <div className="h-4 bg-neutral-200 rounded w-8" />
                      <div className="flex items-center gap-2 justify-end min-w-0">
                        <div className="h-3 bg-neutral-200 rounded w-16" />
                        <div className="w-7 h-7 bg-neutral-100 border border-neutral-200 rounded-full flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data && data.bonus_bet && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3 mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0">
                    <Trophy size={18} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-wider uppercase text-neutral-400">
                      {t("bonus_bet_title")}
                    </div>
                    <div className="font-bold text-xs truncate flex items-center gap-1.5 mt-0.5">
                      {data.bonus_bet.predicted_winner_id ? (
                        <>
                          <TeamFlag team={{ name: data.bonus_bet.team_name, crest_url: data.bonus_bet.team_crest, iso_code: data.bonus_bet.team_iso, flag: data.bonus_bet.team_flag }} size={20} />
                          <span className="text-neutral-800">{data.bonus_bet.team_name}</span>
                        </>
                      ) : (
                        <span className="text-neutral-400 italic">{t("bonus_bet_no_prediction")}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 pl-2">
                  {data.bonus_bet.points !== null ? (
                    <span className={`font-mono-tab text-xs font-bold ${data.bonus_bet.points > 0 ? "text-amber-600" : "text-neutral-400"}`}>
                      +{data.bonus_bet.points} {t("pts")}
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold tracking-wider uppercase text-neutral-400 flex items-center gap-1">
                      <Lock size={12} weight="bold" />
                      {t("locked")}
                    </span>
                  )}
                </div>
              </div>
            )}

            {data && data.bets.length === 0 && !data.bonus_bet && (
              <div className="text-center py-10 text-sm text-neutral-500">{t("no_bets_yet")}</div>
            )}
            {data?.bets.map((b) => {
              const correctExact = b.finished && b.actual_home === b.tip_home && b.actual_away === b.tip_away;
              const pts = activeGroup?.scoring_system === "four_point" ? b.points_four_point : b.points;
              return (
                <div key={b.match_id} className={`rounded-xl border ${correctExact ? "border-[#41a42b]/30 bg-[#41a42b]/8" : "border-neutral-200 bg-white"} p-3 mb-2`}>
                  <div className="flex items-center justify-between text-[10px] font-bold tracking-wider uppercase text-neutral-400 mb-2">
                    <span>{formatDate(b.kickoff_at, locale)}</span>
                    {b.finished ? (
                      <span className={`font-mono-tab ${pts >= 3 ? "text-[#084d25]" : pts > 0 ? "text-[#41a42b]" : "text-neutral-400"}`}>
                        {pts != null ? `+${pts} ${t("pts")}` : "—"}
                      </span>
                    ) : (
                      <span className="text-neutral-400">{t("pending")}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <TeamFlag team={b.home_team} size={28} />
                      <span className="font-bold text-xs truncate">{b.home_team?.name}</span>
                    </div>
                    <div className="text-center">
                      <div className="font-mono-tab text-lg font-bold text-[#02371b] leading-none">
                        {b.tip_home !== null ? b.tip_home : "?"}:{b.tip_away !== null ? b.tip_away : "?"}
                      </div>
                      {b.finished && (
                        <div className="font-mono-tab text-[10px] text-neutral-400 leading-none mt-1">
                          {t("actual")}: {b.actual_home}:{b.actual_away}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 justify-end min-w-0">
                      <span className="font-bold text-xs truncate">{b.away_team?.name}</span>
                      <TeamFlag team={b.away_team} size={28} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
